import React from 'react'

import Icon from 'components_new/atoms/Icon'
import MenuItem from 'components_new/atoms/Menu/MenuItem'
import Select from 'components_new/atoms/Select'
import Switch from 'components_new/atoms/Switch'

import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'
import ToggleButton from 'components_new/molecules/ToggleButton'

import { useTranslation } from 'language/translator'

export type TAutoRefreshMode = 'interval' | 'clock'

// selectable ?refresh intervals, in seconds. The first one is the floor
// enforced by useAutoRefresh (MIN_AUTO_REFRESH_SECONDS).
const INTERVAL_OPTIONS = [
  { labelKey: 'auto_refresh_option_10_minutes', seconds: 600 },
  { labelKey: 'auto_refresh_option_30_minutes', seconds: 1800 },
  { labelKey: 'auto_refresh_option_hourly', seconds: 3600 },
  { labelKey: 'auto_refresh_option_2_hours', seconds: 7200 },
  { labelKey: 'auto_refresh_option_4_hours', seconds: 14400 },
  { labelKey: 'auto_refresh_option_12_hours', seconds: 43200 }
] as const

export const DEFAULT_REFRESH_INTERVAL = INTERVAL_OPTIONS[0].seconds
export const DEFAULT_REFRESH_AT = '06:00'

const pad = (value: number) => (value < 10 ? `0${value}` : String(value))

// Half-hour steps rendered by us rather than a native `type="time"` input,
// which switches to a 12-hour AM/PM clock on locales we do not control.
const CLOCK_OPTIONS = Array.from({ length: 48 }, (unused, index) => {
  return `${pad(Math.floor(index / 2))}:${index % 2 === 0 ? '00' : '30'}`
})

interface AutoRefreshProps {
  mode: TAutoRefreshMode
  refreshAt: string | null
  refreshInterval: number | null
  setMode: (value: TAutoRefreshMode) => void
  setRefreshAt: (value: string | null) => void
  setRefreshInterval: (value: number | null) => void
}

const AutoRefresh = (props: AutoRefreshProps) => {
  const {
    mode,
    refreshAt,
    refreshInterval,
    setMode,
    setRefreshAt,
    setRefreshInterval
  } = props

  const { t } = useTranslation()

  const enabled = refreshInterval !== null || refreshAt !== null

  const handleToggle = () => {
    if (enabled) {
      setRefreshInterval(null)
      setRefreshAt(null)

      return
    }

    if (mode === 'clock') {
      setRefreshAt(DEFAULT_REFRESH_AT)
    } else {
      setRefreshInterval(DEFAULT_REFRESH_INTERVAL)
    }
  }

  const handleModeChange = (value: TAutoRefreshMode) => {
    setMode(value)

    if (!enabled) return

    if (value === 'clock') {
      setRefreshInterval(null)
      setRefreshAt(refreshAt ?? DEFAULT_REFRESH_AT)
    } else {
      setRefreshAt(null)
      setRefreshInterval(refreshInterval ?? DEFAULT_REFRESH_INTERVAL)
    }
  }

  return (
    <SettingsGroup title={t('dashboards', 'auto_refresh_label')}>
      <SettingsItem
        actions={
          <Switch
            color="success"
            checked={enabled}
            onChange={handleToggle}
            size="medium"
          />
        }
        body={t('dashboards', 'auto_refresh_description')}
        icon={<Icon name={'RefreshOutlined'} />}
        title={t('dashboards', 'auto_refresh_label')}
      />

      <SettingsItem
        actions={
          <ToggleButton
            items={[
              {
                title: t('dashboards', 'auto_refresh_mode_clock'),
                value: 'clock'
              },
              {
                title: t('dashboards', 'auto_refresh_mode_interval'),
                value: 'interval'
              }
            ]}
            onChange={(event, value) =>
              handleModeChange(value as TAutoRefreshMode)
            }
            size="small"
            sx={{ width: 224 }}
            value={mode}
          />
        }
        body={t('dashboards', 'auto_refresh_mode_description')}
        disabled={!enabled}
        disabledText={t('dashboards', 'auto_refresh_requirement_note')}
        icon={<Icon name={'TuneOutlined'} />}
        title={t('dashboards', 'auto_refresh_mode_label')}
      />

      {mode === 'clock' ? (
        <SettingsItem
          actions={
            <Select
              onChange={(event) => setRefreshAt(String(event.target.value))}
              sx={{ width: 176 }}
              value={refreshAt ?? DEFAULT_REFRESH_AT}
            >
              {CLOCK_OPTIONS.map((time) => (
                <MenuItem key={time} value={time}>
                  {time}
                </MenuItem>
              ))}
            </Select>
          }
          body={t('dashboards', 'auto_refresh_clock_description')}
          disabled={!enabled}
          disabledText={t('dashboards', 'auto_refresh_requirement_note')}
          icon={<Icon name={'TimerOutlined'} />}
          title={t('dashboards', 'auto_refresh_clock_label')}
          titleAdornments={<code>{'?refresh_at={HH:MM}'}</code>}
        />
      ) : (
        <SettingsItem
          actions={
            <Select
              onChange={(event) =>
                setRefreshInterval(Number(event.target.value))
              }
              sx={{ width: 176 }}
              value={refreshInterval ?? DEFAULT_REFRESH_INTERVAL}
            >
              {INTERVAL_OPTIONS.map((option) => (
                <MenuItem key={option.seconds} value={option.seconds}>
                  {t('dashboards', option.labelKey)}
                </MenuItem>
              ))}
            </Select>
          }
          body={t('dashboards', 'auto_refresh_interval_description')}
          disabled={!enabled}
          disabledText={t('dashboards', 'auto_refresh_requirement_note')}
          icon={<Icon name={'TimerOutlined'} />}
          title={t('dashboards', 'auto_refresh_interval_label')}
          titleAdornments={<code>{'?refresh={seconds}'}</code>}
        />
      )}
    </SettingsGroup>
  )
}

export default AutoRefresh
